import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { useCatalogPowers } from '@/components/painel/powers/powers-cms-context';
import {
  GsImage,
  ORBITAL_ICON_BOX_CLASS,
  copyText,
  getPowerCodeFontSize,
  cn,
} from '@/components/help-center/latest-power-shared';
import {
  GALLERY_BOX_STYLE,
  PowerGalleryViewToggle,
  galleryBadgeClass,
  galleryCodeTextClass,
  usePowerGalleryViewMode,
  type PowerGalleryViewMode,
} from '@/components/help-center/powers/power-gallery-view';
import * as powersService from '@/services/powers.service';
import type { PowerInfo, PowerPawnInfo } from '@/lib/powers-data';
import { MOCK_POWERS } from '@/lib/powers-data';
import { isPubliclyVisible } from '@/lib/cms-publication';

function CopyHoverOverlay({ isCopied }: { isCopied: boolean }) {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 rounded-xl bg-[#000b6a]/92 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
      {isCopied ? (
        <>
          <Check className="h-5 w-5 text-white" />
          <span className="font-poppins text-[10px] font-bold text-white">Copiado!</span>
        </>
      ) : (
        <>
          <Copy className="h-4 w-4 text-white" />
          <span className="font-poppins text-[10px] font-bold text-white">Copiar código</span>
        </>
      )}
    </div>
  );
}

function CardLabel({
  text,
  viewMode,
  isCopied,
  fontSize,
}: {
  text: string;
  viewMode: PowerGalleryViewMode;
  isCopied?: boolean;
  fontSize?: number;
}) {
  return (
    <div className={galleryBadgeClass(viewMode, isCopied)}>
      <span
        className={cn(
          'whitespace-nowrap font-poppins font-semibold leading-none',
          galleryCodeTextClass(viewMode)
        )}
        style={fontSize ? { fontSize: `${fontSize}px` } : undefined}
      >
        {text}
      </span>
    </div>
  );
}

function SmilieCard({
  code,
  itemKey,
  copiedKey,
  onCopy,
  viewMode,
}: {
  code: string;
  itemKey: string;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
  viewMode: PowerGalleryViewMode;
}) {
  const chat = `(${code})`;
  const isCopied = copiedKey === itemKey;
  return (
    <button
      type="button"
      onClick={() => onCopy(itemKey, chat)}
      title={`Copiar ${chat}`}
      className={cn(
        ORBITAL_ICON_BOX_CLASS,
        'group relative flex w-full min-w-0 flex-col items-center gap-2 rounded-xl p-3 transition-shadow duration-200',
        viewMode === 'dark' && 'hover:shadow-lg hover:shadow-blue-950/40'
      )}
      style={GALLERY_BOX_STYLE[viewMode]}
    >
      <CopyHoverOverlay isCopied={isCopied} />
      <GsImage
        code={code}
        className="flex h-14 w-14 items-center justify-center"
        imgClassName="max-h-[52px] max-w-[52px] object-contain"
      />
      <CardLabel
        text={viewMode === 'light' ? chat : code}
        viewMode={viewMode}
        isCopied={isCopied}
        fontSize={getPowerCodeFontSize(viewMode === 'light' ? chat : code)}
      />
    </button>
  );
}

function PawnCard({
  pawn,
  pawnKey,
  copiedKey,
  onCopy,
  viewMode,
}: {
  pawn: PowerPawnInfo;
  pawnKey: string;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
  viewMode: PowerGalleryViewMode;
}) {
  const isCopied = copiedKey === pawnKey;
  return (
    <button
      type="button"
      onClick={() => onCopy(pawnKey, pawn.hat)}
      title={`Copiar ${pawn.hat}`}
      className={cn(
        ORBITAL_ICON_BOX_CLASS,
        'group relative flex w-full min-w-0 flex-col items-center gap-2 rounded-xl p-3 transition-shadow duration-200',
        viewMode === 'dark' && 'hover:shadow-lg hover:shadow-blue-950/40'
      )}
      style={GALLERY_BOX_STYLE[viewMode]}
    >
      <CopyHoverOverlay isCopied={isCopied} />
      <GsImage
        code={pawn.name}
        className="flex h-14 w-14 items-center justify-center"
        imgClassName="max-h-[52px] max-w-[52px] object-contain"
      />
      <CardLabel
        text={pawn.hat}
        viewMode={viewMode}
        isCopied={isCopied}
        fontSize={getPowerCodeFontSize(pawn.hat)}
      />
    </button>
  );
}

function useResolvedPower(slug: string): PowerInfo | null {
  const key = slug.trim().toLowerCase();
  const { powers: catalog } = useCatalogPowers();
  const [remote, setRemote] = useState<PowerInfo | null>(null);

  const fromCatalog = useMemo(() => {
    return (
      catalog.find((p) => p.slug.toLowerCase() === key) ??
      MOCK_POWERS.find((p) => p.slug.toLowerCase() === key) ??
      null
    );
  }, [catalog, key]);

  useEffect(() => {
    if (!key || fromCatalog) {
      setRemote(null);
      return;
    }
    let cancelled = false;
    void powersService
      .getPublicPower(key)
      .then((row) => {
        if (cancelled) return;
        if (row && isPubliclyVisible(row.publicationStatus)) setRemote(row);
        else setRemote(null);
      })
      .catch(() => {
        if (!cancelled) setRemote(null);
      });
    return () => {
      cancelled = true;
    };
  }, [key, fromCatalog]);

  return fromCatalog ?? remote;
}

export function PowerGalleryBbcodeEmbed({
  kind,
  slug,
}: {
  kind: 'smilies' | 'pawns';
  slug: string;
}) {
  const power = useResolvedPower(slug);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const { mode, setMode } = usePowerGalleryViewMode();

  const handleCopy = useCallback(async (itemKey: string, text: string) => {
    const ok = await copyText(text);
    if (ok) {
      setCopiedKey(itemKey);
      setTimeout(() => setCopiedKey((k) => (k === itemKey ? null : k)), 1800);
    }
  }, []);

  if (!power) return null;

  const smilies = power.smilies ?? [];
  const pawns = power.pawns ?? [];
  if (kind === 'smilies' && smilies.length === 0) return null;
  if (kind === 'pawns' && pawns.length === 0) return null;

  return (
    <div className="my-4 w-full overflow-visible rounded-xl border border-gray-200 bg-white/80 p-4">
      <div className="mb-3 flex justify-end">
        <PowerGalleryViewToggle mode={mode} onChange={setMode} className="shrink-0" />
      </div>
      {kind === 'smilies' ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(108px,1fr))] gap-2.5 overflow-visible">
          {smilies.map((code, index) => (
            <SmilieCard
              key={`bb-sm-${index}-${code}`}
              code={code}
              itemKey={`bb-sm-${index}-${code}`}
              copiedKey={copiedKey}
              onCopy={handleCopy}
              viewMode={mode}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(108px,1fr))] gap-2.5 overflow-visible">
          {pawns.map((pawn, i) => (
            <PawnCard
              key={`bb-pawn-${i}-${pawn.hat}`}
              pawn={pawn}
              pawnKey={`bb-pawn-${i}-${pawn.hat}`}
              copiedKey={copiedKey}
              onCopy={handleCopy}
              viewMode={mode}
            />
          ))}
        </div>
      )}
    </div>
  );
}
